import { PropsWithChildren, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { FormKey } from '~/app/[:store]/order/[:idFromPlatform]/[:claimType]/[:draftId]/resolution/resolution-action';
import Button from '~/components/button';
import Submit from '~/components/button-submit';
import Form from '~/components/form';
import ProductImage from '~/components/product-image';
import { ResolutionMethod } from '~/utils/constants';

import { useResolutionData } from '~/app/[:store]/order/[:idFromPlatform]/[:claimType]/[:draftId]/resolution/resolution-loader';
import { AdvancedExchangeMethod } from '~/utils/schemas';
import {
  useExchangeProducts,
  useExchangeSelection,
  variantsForOptions,
} from './exchange-options';
import { useOptionAvailability } from './use-option-availability';

export const computeBalance = (
  advancedExchangeMethod: AdvancedExchangeMethod,
  lineItemPrice: number,
  variantPrice: number,
) => {
  const balance = variantPrice - lineItemPrice;

  if (
    advancedExchangeMethod === 'NO_PAYMENT_OR_REFUND' ||
    (balance <= 0 && advancedExchangeMethod === 'PAYMENT') ||
    (balance === 0 && advancedExchangeMethod === 'PAYMENT_AND_PARTIAL_REFUND')
  )
    return null;

  return {
    amount: Math.abs(balance),
    type: balance > 0 ? 'Payment' : 'Credit',
  };
};

function VariantOptionButton({
  selected = false,
  available = true,
  onClick,
  children,
}: PropsWithChildren<{
  selected?: boolean;
  available?: boolean;
  onClick?: () => void;
}>) {
  return (
    <Button
      type="button"
      disabled={!available}
      variant={selected ? 'filled' : 'outlined'}
      className={twMerge(
        'rounded-sm px-3 py-2 text-xs',
        !available &&
          'cursor-not-allowed border-gray-200 bg-gray-200 text-gray-500 hover:bg-gray-200',
        available && !selected && 'hover:bg-gray-100',
      )}
      onClick={onClick}
      aria-pressed={selected}
    >
      {children}
    </Button>
  );
}

function ExchangeBalance() {
  const [{ selectedProduct, selectedVariant }] = useExchangeSelection();

  const { currencyFormatter } = useResolutionData();
  const {
    draftClaimLineItem: {
      lineItem: { unitPrice: lineItemPrice },
    },
  } = useResolutionData();

  if (!selectedProduct || !selectedVariant?.price) return null;

  const balance = computeBalance(
    selectedProduct.advancedExchangeMethod,
    lineItemPrice,
    selectedVariant.price,
  );

  if (!balance) return null;

  const balanceMessage = `You'll ${balance.type === 'Payment' ? 'pay an additional' : 'get back'}
  ${currencyFormatter(balance.amount)}`;

  return (
    <p className="mt-2 items-center">
      <span className="text-corso-gray-500">{balanceMessage}</span>
    </p>
  );
}

export default function ProductVariantSelector() {
  const submitRef = useRef<HTMLButtonElement>(null);
  const exchangeProducts = useExchangeProducts();

  const [{ selectedProduct, selectedVariant, selectedOptions }, dispatch] =
    useExchangeSelection();
  const optionAvailability = useOptionAvailability();

  if (!selectedProduct) return null;

  const possibleVariant = variantsForOptions(
    selectedProduct,
    selectedOptions,
  ).find((v) => v.imgUrl);

  return (
    <>
      {exchangeProducts.length > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="text"
            className="flex w-fit items-center gap-2"
            onClick={() => dispatch({ type: 'setProduct', product: null })}
          >
            <span className="text-md">← Back To Products</span>
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 justify-items-center gap-8 md:grid-cols-2 md:justify-items-stretch">
        <div className="size-72">
          <ProductImage
            alt={selectedVariant?.titleWithOptions || selectedProduct.name}
            src={
              selectedVariant?.imgUrl ||
              possibleVariant?.imgUrl ||
              selectedProduct.imageUrl
            }
            variant="full"
          />
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-left text-lg font-medium text-gray-800">
            {selectedProduct.name}
          </p>
          {!selectedProduct.hasOnlyDefaultVariant &&
            selectedProduct.options.map((option) => (
              <fieldset key={option.name} className="space-y-2">
                <legend className="text-md text-left text-gray-800">
                  {option.name}
                </legend>
                <div className="flex flex-wrap gap-2">
                  {option.values.map((value) => {
                    const { available, selected } = optionAvailability({
                      name: option.name,
                      value,
                    });
                    return (
                      <VariantOptionButton
                        key={value}
                        available={available}
                        selected={selected}
                        onClick={() =>
                          dispatch({
                            type: 'toggleOption',
                            option: { name: option.name, value },
                          })
                        }
                      >
                        {value}
                      </VariantOptionButton>
                    );
                  })}
                </div>
              </fieldset>
            ))}
        </div>
      </div>

      <Form
        className="gap-4"
        name="variantExchange"
        data-testid="variantExchangeForm"
      >
        <input
          type="hidden"
          hidden
          name={FormKey.resolution}
          data-testid="exchangeResolution"
          defaultValue={ResolutionMethod.variantExchange}
        />

        <input
          type="hidden"
          hidden
          name={FormKey.advancedExchangeMethod}
          data-testid="advancedExchangeMethod"
          defaultValue={selectedProduct.advancedExchangeMethod}
        />

        <input
          type="hidden"
          hidden
          name={FormKey.variant}
          data-testid={FormKey.variant}
          defaultValue={JSON.stringify(selectedVariant)}
        />

        <div className="flex flex-col items-start">
          <div className="flex w-full flex-col gap-2">
            <Submit disabled={!selectedVariant} ref={submitRef}>
              Exchange
            </Submit>
            <ExchangeBalance />
          </div>
        </div>
      </Form>
    </>
  );
}
